import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { dataRelativa, plural, textoPuroDeHtml, type CategoriaResumo, type Nota } from "@noesis/shared";
import { CELULAR, useConsulta } from "../componentes/largura.js";
import { useSessao } from "../estado/sessao.js";
import { FalhaDaApi, pedir } from "../api.js";
import { CabecalhoDePagina } from "../componentes/CabecalhoDePagina.js";
import { Icone } from "../componentes/Icone.js";
import { MenuDeAcoes } from "../componentes/Controles.js";
import {
  IconeBuscar,
  IconeChevron,
  IconeCriar,
  IconeLink,
  IconeFechar,
  IconePagina,
} from "../componentes/icones.js";
import { ConteudoRico } from "../caderno/ConteudoRico.js";
import { EditorRico } from "../caderno/EditorRico.js";
import { useTrilhaAtiva, useTrilhas } from "../estado/trilhas.js";

/**
 * Caderno — RN-29, refeito na Fase 2 das fases corretivas.
 *
 * **Duas colunas, e o editor sempre aberto.** Antes eram duas telas: uma grade
 * de cartões com uma árvore de três níveis à esquerda, e uma segunda rota com o
 * editor. Escrever exigia navegar até o texto, e a árvore respondia a uma
 * pergunta — "em que pasta isto está?" — que não é a que se faz ao abrir o
 * caderno. A pergunta é "onde eu escrevo agora", e a resposta dela é o texto na
 * tela. Agora `/caderno` e `/caderno/:id` são a **mesma tela**: à esquerda a
 * árvore de matérias com as páginas dentro, à direita o editor com a página
 * aberta. Sem id, abre a última editada.
 *
 * **Dois níveis, não três.** Matéria → página. O vínculo com assunto continua
 * no banco, no `categoriaId` da nota, e continua sendo gravado — ele só deixa de
 * aparecer na árvore. Uma árvore de três níveis num acervo de dez páginas é
 * mais gesto de abrir pasta do que página para ler.
 *
 * `html` é a fonte da verdade (RN-39, ADR 012). O editor é da família
 * ProseMirror, com **marcas próprias que devolvem classe em vez de atributo
 * `style`** — sem isso a CSP bloquearia a formatação e a falha apareceria no
 * conteúdo do usuário. Ver `caderno/extensoes.ts` e o portão em
 * `caderno/portao-csp.portao.test.ts`.
 */

/** O que a árvore precisa saber de cada página. */
type ResumoDePagina = {
  id: string;
  titulo: string;
  categoria: string[];
  resumo: string;
  atualizadoEm: string;
};

/** O nome que uma página sem nome carrega, na árvore e no editor. */
const SEM_TITULO = "Sem título";

/** A matéria de uma página é a raiz do caminho dela. */
function materiaDe(caminho: readonly string[]): string | null {
  return caminho[0] ?? null;
}

export function Caderno() {
  const trilha = useTrilhaAtiva();
  const { estado: trilhas } = useTrilhas();
  const { id } = useParams();
  const navegar = useNavigate();

  const [categorias, setCategorias] = useState<CategoriaResumo[]>([]);
  const [paginas, setPaginas] = useState<ResumoDePagina[]>([]);
  const [carregandoArvore, setCarregandoArvore] = useState(true);
  const [erroDaArvore, setErroDaArvore] = useState<string | null>(null);
  const [gaveta, setGaveta] = useState(false);
  const [criando, setCriando] = useState(false);
  // `ref` além do estado: dois toques no mesmo quadro leriam o `false` velho.
  const criandoRef = useRef(false);

  /* -------------------------------------------------------------- a árvore */

  const recarregarArvore = useCallback(async () => {
    if (!trilha) return;
    try {
      const [c, n] = await Promise.all([
        pedir("categoria.listar"),
        pedir("nota.consultar", { corpo: { termo: "", categoriaId: null } }),
      ]);
      setCategorias(c.categorias);
      setPaginas(
        n.notas.map((x) => ({ id: x.id, titulo: x.titulo, categoria: x.categoria, resumo: x.resumo, atualizadoEm: x.atualizadoEm })),
      );
      setErroDaArvore(null);
    } catch (e) {
      setErroDaArvore(e instanceof FalhaDaApi ? e.message : "Não foi possível carregar as páginas.");
    } finally {
      setCarregandoArvore(false);
    }
  }, [trilha]);

  useEffect(() => {
    void recarregarArvore();
  }, [recarregarArvore]);

  /*
   * Sem id na URL, abre a **última editada**. A consulta já vem ordenada por
   * `atualizadoEm` decrescente (`apps/api/src/caderno.ts`), então é a primeira.
   * `replace` e não `push`: a tela não é um destino separado, e empilhar um
   * histórico faria o botão de voltar do navegador passar duas vezes por aqui.
   */
  // No celular a tela sem id é a lista de páginas (13d); no desktop, a última
  // editada abre direto.
  const celular = useConsulta(CELULAR);
  useEffect(() => {
    if (id || carregandoArvore || paginas.length === 0 || celular) return;
    void navegar(`/caderno/${paginas[0]!.id}`, { replace: true });
  }, [id, carregandoArvore, paginas, navegar, celular]);

  /** As matérias do contexto, **todas**, inclusive as sem página nenhuma. */
  const materias = useMemo(() => {
    const raizes = categorias.filter((c) => c.profundidade === 0).map((c) => ({ id: c.id, nome: c.nome }));
    // Página cuja matéria não está entre as categorias — nota sem pasta, ou
    // pasta apagada. Ela precisa de um grupo, senão some da árvore em silêncio.
    const orfas = new Set<string>();
    for (const p of paginas) {
      const m = materiaDe(p.categoria);
      if (m === null) orfas.add(SEM_MATERIA);
      else if (!raizes.some((r) => r.nome === m)) orfas.add(m);
    }
    return [...raizes, ...[...orfas].map((nome) => ({ id: null, nome }))];
  }, [categorias, paginas]);

  const criarPagina = useCallback(
    async (materiaId: string | null) => {
      // Retorno antes da resposta (Fase 6A): o alvo fica desabilitado enquanto a
      // viagem acontece, e um segundo toque não cria duas páginas.
      if (criandoRef.current) return;
      criandoRef.current = true;
      setCriando(true);
      try {
        const r = await pedir("nota.criar", {
          corpo: { titulo: SEM_TITULO, html: "", categoriaId: materiaId, itensAnexados: [] },
        });
        await recarregarArvore();
        void navegar(`/caderno/${r.nota.id}`);
      } catch (e) {
        setErroDaArvore(e instanceof FalhaDaApi ? e.message : "Não foi possível criar a página.");
      } finally {
        criandoRef.current = false;
        setCriando(false);
      }
    },
    [navegar, recarregarArvore],
  );

  /** A matéria em que um "+ Nova página" do cabeçalho vai escrever. */
  const materiaDaPaginaAberta = useMemo(() => {
    const aberta = paginas.find((p) => p.id === id);
    const nome = aberta ? materiaDe(aberta.categoria) : null;
    const achada = categorias.find((c) => c.profundidade === 0 && c.nome === nome);
    return achada?.id ?? categorias.find((c) => c.profundidade === 0)?.id ?? null;
  }, [paginas, id, categorias]);

  /* Mesma razão do Diagnóstico: a coluna estreita do estado "sem trilha" trocada
     pela ampla um quadro depois custava 0,14 de CLS. Ver lá. */
  if (trilhas.nome !== "pronto") {
    return (
      <div className="tela tela--ampla tela--painel">
        <div className="tela__corpo" aria-busy="true" data-esqueleto="visivel">
          <div className="esqueleto esqueleto--titulo" />
          <div className="esqueleto esqueleto--faixa" />
        </div>
      </div>
    );
  }

  if (!trilha) {
    return (
      <div className="tela">
        <div className="tela__corpo">
          <CabecalhoDePagina eyebrow="Caderno" titulo="Suas anotações" />
          <section className="estado">
            <h2 className="estado__titulo">Nenhuma trilha em uso</h2>
            <p className="estado__texto">
              As páginas pertencem a uma trilha: elas mudam quando você troca de frente de estudo.
            </p>
            <Link className="botao" to="/trilhas">
              Escolher uma trilha
            </Link>
          </section>
        </div>
      </div>
    );
  }

  if (celular && !id && !carregandoArvore) {
    return (
      <ListaMovel
        paginas={paginas}
        criando={criando}
        aoCriar={() => void criarPagina(materiaDaPaginaAberta)}
      />
    );
  }

  return (
    <div className="tela tela--ampla tela--painel">
      <div className="tela__corpo caderno">
        <CabecalhoDePagina
          titulo="Suas anotações"
          subtitulo="Páginas de teoria e resumo, organizadas por matéria."
          acoes={
            <button
              type="button"
              className="botao"
              disabled={criando}
              onClick={() => void criarPagina(materiaDaPaginaAberta)}
            >
              <Icone glifo={IconeCriar} papel="acao" />
              {criando ? "Criando…" : "Nova página"}
            </button>
          }
        />

        {erroDaArvore && (
          <p className="campo__erro" role="alert">
            {erroDaArvore}
          </p>
        )}

        {/*
          No estreito a árvore vira gaveta, e o botão que a abre fica acima do
          editor. Quem decide o que aparece é a folha, e não `matchMedia`:
          montar uma ou outra em JavaScript deixaria a primeira pintura sem
          navegação e faria a tela piscar ao redimensionar.
        */}
        <button
          type="button"
          className="botao botao--secundario botao--compacto caderno__abrir-materias"
          onClick={() => setGaveta(true)}
        >
          Matérias
        </button>
        <Link className="link-mao link-mao--tinta caderno__voltar-lista" to="/caderno">
          <Icone glifo="chevron_left" papel="acao" tamanho="p" />
          páginas
        </Link>

        <div className="caderno__quadro">
          <Navegacao
            materias={materias}
            paginas={paginas}
            abertaId={id ?? null}
            carregando={carregandoArvore}
            emGaveta={gaveta}
            aoFecharGaveta={() => setGaveta(false)}
            aoCriar={(materiaId) => void criarPagina(materiaId)}
            aoEscolher={() => setGaveta(false)}
          />

          {/*
            **Enquanto a árvore não chegou, a coluna da direita é esqueleto** —
            e não o estado vazio (Fase 6A).
            Sem id a tela ainda não sabe se há página nenhuma ou se ela está a
            caminho: pintar "Sem páginas ainda" no intervalo e trocar pelo editor
            um quadro depois custou **0,14 de CLS** na medida da fase, que é
            acima do limite de 0,1. O estado vazio só aparece quando ele é
            verdade.
          */}
          {carregandoArvore ? (
            <section className="caderno__editor" aria-busy="true" data-esqueleto="visivel">
              <div className="esqueleto esqueleto--titulo" />
              <div className="esqueleto esqueleto--bloco" />
            </section>
          ) : id ? (
            <Editor
              key={id}
              id={id}
              categorias={categorias}
              materias={materias}
              paginas={paginas}
              aoMudarAArvore={() => void recarregarArvore()}
            />
          ) : (
            <section className="caderno__editor estado">
              <h2 className="estado__titulo">Sem páginas ainda</h2>
              <p className="estado__texto">Crie a primeira.</p>
              <button type="button" className="botao" disabled={criando} onClick={() => void criarPagina(materiaDaPaginaAberta)}>
                <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
                {criando ? "Criando…" : "Nova página"}
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

/** O grupo das páginas cuja nota não aponta para pasta nenhuma. */
const SEM_MATERIA = "Sem matéria";

/* ------------------------------------------------------- painel da esquerda */

type Materia = { id: string | null; nome: string };

/**
 * A navegação: busca, rótulo e a árvore de dois níveis.
 *
 * **A busca filtra em duas frentes.** O título é filtrado aqui mesmo, a cada
 * tecla, sem ida ao servidor — é resposta imediata sobre um dado que já está na
 * mão. O corpo é filtrado pelo servidor (`nota.consultar`), que procura no
 * **texto puro** e não no HTML: buscar "cor" não pode trazer toda página que
 * tem um trecho colorido. As duas listas se somam.
 */
function Navegacao({
  materias,
  paginas,
  abertaId,
  carregando,
  emGaveta,
  aoFecharGaveta,
  aoCriar,
  aoEscolher,
}: {
  materias: readonly Materia[];
  paginas: readonly ResumoDePagina[];
  abertaId: string | null;
  carregando: boolean;
  emGaveta: boolean;
  aoFecharGaveta: () => void;
  aoCriar: (materiaId: string | null) => void;
  aoEscolher: () => void;
}) {
  const [termo, setTermo] = useState("");
  const [doCorpo, setDoCorpo] = useState<Set<string> | null>(null);

  useEffect(() => {
    const limpo = termo.trim();
    if (limpo === "") {
      setDoCorpo(null);
      return;
    }
    let vivo = true;
    const t = setTimeout(() => {
      void pedir("nota.consultar", { corpo: { termo: limpo, categoriaId: null } })
        .then((r) => {
          if (vivo) setDoCorpo(new Set(r.notas.map((n) => n.id)));
        })
        .catch(() => {
          // Sem o servidor sobra o filtro por título, que é local. Buscar
          // offline trazendo menos é melhor do que a busca parecer quebrada.
          if (vivo) setDoCorpo(new Set());
        });
    }, 200);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [termo]);

  const buscando = termo.trim() !== "";
  const visiveis = useMemo(() => {
    if (!buscando) return paginas;
    const alvo = termo.trim().toLocaleLowerCase("pt-BR");
    return paginas.filter(
      (p) => p.titulo.toLocaleLowerCase("pt-BR").includes(alvo) || (doCorpo?.has(p.id) ?? false),
    );
  }, [buscando, termo, paginas, doCorpo]);

  const porMateria = useMemo(() => {
    const mapa = new Map<string, ResumoDePagina[]>();
    for (const p of visiveis) {
      const chave = materiaDe(p.categoria) ?? SEM_MATERIA;
      const lista = mapa.get(chave) ?? [];
      lista.push(p);
      mapa.set(chave, lista);
    }
    return mapa;
  }, [visiveis]);

  const materiaAberta = useMemo(() => {
    const p = paginas.find((x) => x.id === abertaId);
    return p ? (materiaDe(p.categoria) ?? SEM_MATERIA) : null;
  }, [paginas, abertaId]);

  /* Com busca ativa, matéria sem resultado some — e as com resultado abrem. */
  const listadas = buscando ? materias.filter((m) => (porMateria.get(m.nome)?.length ?? 0) > 0) : materias;

  return (
    <nav
      className={emGaveta ? "caderno__navegacao caderno__navegacao--aberta" : "caderno__navegacao"}
      aria-label="Páginas do caderno"
    >
      <div className="caderno__busca">
        <Icone glifo={IconeBuscar} papel="decorativo" tamanho="p" className="caderno__lupa" />
        <input
          className="campo__entrada caderno__campo-busca"
          type="search"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="buscar nas páginas"
          aria-label="Buscar nas páginas"
        />
        <button type="button" className="botao botao--texto caderno__fechar-gaveta" onClick={aoFecharGaveta}>
          <Icone glifo={IconeFechar} papel="acao" tamanho="p" />
          Fechar
        </button>
      </div>

      <p className="so-leitor">Matérias</p>

      {carregando ? (
        <div className="esqueleto esqueleto--bloco" aria-hidden="true" />
      ) : listadas.length === 0 ? (
        <p className="campo__ajuda">
          {buscando ? "Nenhuma página com esse termo." : "Nenhuma matéria ainda. Elas nascem da importação."}
        </p>
      ) : (
        <ul className="caderno__materias">
          {listadas.map((m) => (
            <Materia
              key={m.nome}
              materia={m}
              paginas={porMateria.get(m.nome) ?? []}
              abertaId={abertaId}
              nasceAberta={buscando || m.nome === materiaAberta}
              aoCriar={aoCriar}
              aoEscolher={aoEscolher}
            />
          ))}
        </ul>
      )}
    </nav>
  );
}

function Materia({
  materia,
  paginas,
  abertaId,
  nasceAberta,
  aoCriar,
  aoEscolher,
}: {
  materia: Materia;
  paginas: readonly ResumoDePagina[];
  abertaId: string | null;
  nasceAberta: boolean;
  aoCriar: (materiaId: string | null) => void;
  aoEscolher: () => void;
}) {
  const [aberta, setAberta] = useState(nasceAberta);

  /* A busca abre e fecha matérias por fora: seguir o cálculo é o que faz a
     lista responder ao que se digita em vez de congelar no estado do primeiro
     render. */
  useEffect(() => {
    if (nasceAberta) setAberta(true);
  }, [nasceAberta]);

  return (
    <li className="caderno__materia">
      <button
        type="button"
        className="caderno__linha-materia"
        aria-expanded={aberta}
        onClick={() => setAberta((v) => !v)}
      >
        <Icone
          glifo={IconeChevron}
          papel="acao"
          tamanho="p"
          className={aberta ? "caderno__chevron caderno__chevron--aberto" : "caderno__chevron"}
        />
        <span className="caderno__nome-materia">{materia.nome}</span>
        <span className="dado caderno__conta">{paginas.length}</span>
      </button>

      {/*
        O grupo fica **montado** nos dois estados, e é o que permite animar a
        altura (Fase 7): conteúdo que desmonta não tem de onde sair. `inert`
        fecha a porta do teclado e do leitor de tela enquanto ele está recolhido
        — sem isso a tabulação entraria numa lista invisível.
      */}
      <div className={aberta ? "acordeao acordeao--aberto" : "acordeao"} inert={!aberta}>
        <ul className="caderno__paginas">
          {paginas.map((p) => (
            <li key={p.id}>
              <Link
                to={`/caderno/${p.id}`}
                className={p.id === abertaId ? "caderno__pagina caderno__pagina--ativa" : "caderno__pagina"}
                aria-current={p.id === abertaId ? "page" : undefined}
                onClick={aoEscolher}
              >
                <span className="caderno__nome-pagina">{p.titulo.trim() === "" ? SEM_TITULO : p.titulo}</span>
              </Link>
            </li>
          ))}
          {/* Só em matéria de verdade: sem id não há pasta em que gravar. */}
          {materia.id !== null && (
            <li>
              <button type="button" className="link-mao caderno__criar" onClick={() => aoCriar(materia.id)}>
                <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
                página
              </button>
            </li>
          )}
        </ul>
      </div>
    </li>
  );
}

/* ---------------------------------------------------------- painel do editor */

type EstadoDoSalvamento =
  | { nome: "limpo" }
  | { nome: "salvando" }
  | { nome: "salvo"; em: number }
  | { nome: "erro" };

export function Editor({
  id,
  categorias,
  materias,
  paginas,
  aoMudarAArvore,
}: {
  id: string;
  categorias: readonly CategoriaResumo[];
  materias: readonly Materia[];
  paginas: readonly ResumoDePagina[];
  aoMudarAArvore: () => void;
}) {
  const navegar = useNavigate();

  const [estado, setEstado] = useState<"carregando" | "pronto" | "erro">("carregando");
  const [mensagemDeErro, setMensagemDeErro] = useState("");
  const [titulo, setTitulo] = useState("");
  const [html, setHtml] = useState("");
  const [categoriaId, setCategoriaId] = useState<string | null>(null);
  const [anexados, setAnexados] = useState<string[]>([]);
  const [base, setBase] = useState<string | null>(null);
  const [salvamento, setSalvamento] = useState<EstadoDoSalvamento>({ nome: "limpo" });
  const [conflito, setConflito] = useState<{ suaVersao: { titulo: string; html: string }; versaoDoServidor: Nota } | null>(null);

  const corpo = useRef<HTMLDivElement>(null);

  /*
   * Gravação por debounce (RN-44). O que a `ref` guarda é sempre a última
   * versão digitada: o `setTimeout` e os ouvintes de flush disparam fora do
   * render e leriam estado velho de dentro de uma closure.
   *
   * `sujo` é o que impede o flush de gravar uma página que ninguém tocou — sem
   * ele, abrir e fechar uma página criaria uma versão nova a cada visita.
   */
  const atual = useRef({ titulo: "", html: "", categoriaId: null as string | null, anexados: [] as string[], base: null as string | null });
  const sujo = useRef(false);
  const gravarRef = useRef<() => Promise<void>>(async () => {});

  useEffect(() => {
    let vivo = true;
    setEstado("carregando");
    void pedir("nota.ver", { params: { id } })
      .then((r) => {
        if (!vivo) return;
        setTitulo(r.nota.titulo);
        setHtml(r.nota.html);
        setCategoriaId(r.nota.categoriaId);
        setAnexados(r.nota.itensAnexados);
        setBase(r.nota.atualizadoEm);
        sujo.current = false;
        setSalvamento({ nome: "limpo" });
        setEstado("pronto");
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setMensagemDeErro(e instanceof FalhaDaApi ? e.message : "Não foi possível abrir a página.");
        setEstado("erro");
      });
    return () => {
      vivo = false;
    };
  }, [id]);

  atual.current = { titulo, html, categoriaId, anexados, base };

  const gravar = useCallback(async () => {
    const v = atual.current;
    if (!sujo.current || v.base === null) return;
    sujo.current = false;
    setSalvamento({ nome: "salvando" });
    try {
      const r = await pedir("nota.salvar", {
        params: { id },
        corpo: {
          // Página sem nome grava com o rótulo que a árvore mostra: o contrato
          // exige título, e o que a Fase 2 pede é que ninguém seja obrigado a
          // dar nome antes de escrever.
          titulo: v.titulo.trim() === "" ? SEM_TITULO : v.titulo,
          html: v.html,
          categoriaId: v.categoriaId,
          itensAnexados: v.anexados,
          baseAtualizadoEm: v.base,
        },
      });
      if (r.resultado === "conflito") {
        setConflito({ suaVersao: r.suaVersao, versaoDoServidor: r.versaoDoServidor });
        setSalvamento({ nome: "limpo" });
        return;
      }
      setBase(r.nota.atualizadoEm);
      setSalvamento({ nome: "salvo", em: Date.now() });
      aoMudarAArvore();
    } catch {
      // Continua sujo: a próxima parada de digitação tenta de novo, e o
      // indicador oferece a tentativa manual.
      sujo.current = true;
      setSalvamento({ nome: "erro" });
    }
  }, [id, aoMudarAArvore]);

  gravarRef.current = gravar;

  /*
   * RN-44: debounce de 800ms. Ele existe para a gravação não ser uma requisição
   * por tecla; os dois flushes abaixo existem porque debounce sozinho perde
   * exatamente a última edição, que é a que a pessoa acabou de escrever.
   */
  useEffect(() => {
    if (!sujo.current || conflito) return;
    const t = setTimeout(() => void gravarRef.current(), 800);
    return () => clearTimeout(t);
  }, [titulo, html, categoriaId, anexados, conflito]);

  /*
   * `visibilitychange` cobre trocar de aba, bloquear o telefone e fechar o
   * navegador no celular — onde `beforeunload` não é confiável. O desmontar
   * cobre sair para outra rota e trocar de página dentro da própria árvore,
   * porque a `key` do componente é o id.
   */
  useEffect(() => {
    const aoEsconder = () => {
      if (document.visibilityState === "hidden") void gravarRef.current();
    };
    document.addEventListener("visibilitychange", aoEsconder);
    return () => {
      document.removeEventListener("visibilitychange", aoEsconder);
      void gravarRef.current();
    };
  }, []);

  const palavras = useMemo(() => {
    const puro = textoPuroDeHtml(html).trim();
    return puro === "" ? 0 : puro.split(/\s+/).length;
  }, [html]);

  const vizinha = useMemo(() => {
    const i = paginas.findIndex((p) => p.id === id);
    if (i === -1) return null;
    return paginas[i + 1] ?? paginas[i - 1] ?? null;
  }, [paginas, id]);

  async function excluir() {
    try {
      await pedir("nota.excluir", { params: { id } });
      // Não grave o que acabou de ser apagado.
      sujo.current = false;
      aoMudarAArvore();
      void navegar(vizinha ? `/caderno/${vizinha.id}` : "/caderno", { replace: true });
    } catch (e) {
      setMensagemDeErro(e instanceof FalhaDaApi ? e.message : "Não foi possível excluir a página.");
    }
  }

  if (estado === "carregando") {
    return (
      <section className="caderno__editor" aria-busy="true">
        <div className="esqueleto esqueleto--titulo" />
        <div className="esqueleto esqueleto--bloco" />
      </section>
    );
  }

  if (estado === "erro") {
    return (
      <section className="caderno__editor estado estado--erro" role="alert">
        <h2 className="estado__titulo">Não deu para abrir</h2>
        <p className="estado__texto">{mensagemDeErro}</p>
        <Link className="botao" to="/caderno">
          Voltar ao caderno
        </Link>
      </section>
    );
  }

  return (
    <section className="caderno__editor" aria-label="Página aberta">
      <div className="caderno__barra-topo">
        <label className="caderno__pilula">
          <span className="so-leitor">Matéria da página</span>
          <select
            className="caderno__select-materia"
            value={categoriaId ?? ""}
            onChange={(e) => {
              sujo.current = true;
              setCategoriaId(e.target.value === "" ? null : e.target.value);
            }}
          >
            <option value="">Sem matéria</option>
            {materias
              .filter((m): m is { id: string; nome: string } => m.id !== null)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nome}
                </option>
              ))}
            {/*
              A pasta atual quando ela é um **assunto**, e não a matéria. Páginas
              antigas apontam para o assunto, e o vínculo continua no banco; sem
              esta opção o `select` mostraria a página como se ela não tivesse
              pasta, e a primeira gravação apagaria o vínculo em silêncio.
            */}
            {categoriaId !== null && !materias.some((m) => m.id === categoriaId) && (
              <option value={categoriaId}>
                {categorias.find((c) => c.id === categoriaId)?.caminho.join(" › ") ?? "Pasta atual"}
              </option>
            )}
          </select>
        </label>

        <div className="caderno__estado">
          <IndicadorDeSalvamento estado={salvamento} aoTentarDeNovo={() => void gravar()} />
          <span className="dado caderno__palavras">{plural(palavras, "palavra")}</span>
          {/* Excluir sai do topo e vai para o menu ⋯, que pergunta antes (ADR 039). */}
          <MenuDeAcoes
            rotulo="Excluir a página"
            gatilho={{ texto: "excluir", glifo: "lixeira" }}
            acoes={[
              {
                rotulo: "Excluir página",
                destrutiva: true,
                confirmacao: "Excluir esta página? O texto não volta.",
                aoEscolher: () => void excluir(),
              },
            ]}
          />
        </div>
      </div>

      {conflito && (
        <Conflito
          conflito={conflito}
          aoUsarDoServidor={(n) => {
            setTitulo(n.titulo);
            setHtml(n.html);
            setBase(n.atualizadoEm);
            // Adotar a do servidor não é edição: gravar de volta por cima dela
            // seria criar uma versão nova idêntica.
            sujo.current = false;
            setConflito(null);
          }}
          aoManterASua={() => {
            // Adota a versão do servidor como base e regrava por cima — decisão
            // explícita do usuário, nunca automática.
            setBase(conflito.versaoDoServidor.atualizadoEm);
            sujo.current = true;
            setConflito(null);
          }}
        />
      )}

      {mensagemDeErro && (
        <p className="campo__erro" role="alert">
          {mensagemDeErro}
        </p>
      )}

      <input
        className="caderno__titulo"
        value={titulo}
        placeholder={SEM_TITULO}
        aria-label="Título da página"
        maxLength={200}
        onChange={(e) => {
          sujo.current = true;
          setTitulo(e.target.value);
        }}
        onKeyDown={(e) => {
          // Enter no título leva ao corpo: é a sequência de quem acabou de dar
          // nome à página e vai escrever.
          if (e.key === "Enter") {
            e.preventDefault();
            corpo.current?.querySelector<HTMLElement>(".editor-rico__area")?.focus();
          }
        }}
      />

      <div ref={corpo} className="caderno__corpo">
        <EditorRico
          html={html}
          rotulo="Texto da página"
          aoSalvar={() => {
            sujo.current = true;
            void gravar();
          }}
          aoMudar={(novo) => {
            sujo.current = true;
            setHtml(novo);
          }}
          rodape={
            anexados.length > 0 ? (
              <p className="caderno__vinculos">
                <span className="chip-legivel">
                  <Icone glifo={IconeLink} papel="acao" tamanho="p" />
                  {plural(anexados.length, "questão vinculada", "questões vinculadas")}
                </span>
              </p>
            ) : null
          }
        />
      </div>
    </section>
  );
}

/**
 * O indicador de salvamento — ponto e frase.
 *
 * Ele conta o tempo por conta própria, num intervalo de trinta segundos: sem
 * isso "Salvo agora" ficaria escrito na tela meia hora depois de o salvamento
 * ter acontecido, que é pior do que não dizer nada.
 */
function IndicadorDeSalvamento({
  estado,
  aoTentarDeNovo,
}: {
  estado: EstadoDoSalvamento;
  aoTentarDeNovo: () => void;
}) {
  const [, redesenhar] = useState(0);

  useEffect(() => {
    if (estado.nome !== "salvo") return;
    const t = setInterval(() => redesenhar((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, [estado]);

  if (estado.nome === "limpo") return null;

  if (estado.nome === "erro") {
    return (
      <button type="button" className="botao botao--texto caderno__salvo caderno__salvo--erro" onClick={aoTentarDeNovo}>
        <span className="caderno__ponto" aria-hidden="true" />
        Erro ao salvar — tentar de novo
      </button>
    );
  }

  if (estado.nome === "salvando") {
    return (
      <p className="metadado caderno__salvo" role="status">
        <span className="caderno__ponto caderno__ponto--ativo" aria-hidden="true" />
        Salvando…
      </p>
    );
  }

  return (
    <p className="metadado caderno__salvo" role="status">
      <span className="caderno__ponto" aria-hidden="true" />
      {textoDoSalvo(Date.now() - estado.em)}
    </p>
  );
}

/** "Salvo agora", "Salvo há 5 min", "Salvo há 2 h", "Salvo há 1 d". */
export function textoDoSalvo(decorridoMs: number): string {
  const minutos = Math.floor(decorridoMs / 60_000);
  if (minutos < 1) return "Salvo agora";
  if (minutos < 60) return `Salvo há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `Salvo há ${plural(horas, "h", "h")}`;
  return `Salvo há ${plural(Math.floor(horas / 24), "d", "d")}`;
}

/**
 * Conflito de edição (RN-29). As **duas versões** são mostradas e a escolha é
 * do usuário — nada é sobrescrito por decisão do programa.
 */
function Conflito({
  conflito,
  aoUsarDoServidor,
  aoManterASua,
}: {
  conflito: { suaVersao: { titulo: string; html: string }; versaoDoServidor: Nota };
  aoUsarDoServidor: (n: Nota) => void;
  aoManterASua: () => void;
}) {
  return (
    <section className="cartao pilha confirmacao" role="alertdialog" aria-labelledby="conflito-titulo">
      <h2 className="estado__titulo" id="conflito-titulo">
        Esta página mudou enquanto você escrevia
      </h2>
      <p className="estado__texto">
        Outra aba ou outro aparelho salvou uma versão depois que esta tela carregou. Nada foi sobrescrito. As duas
        estão aqui — escolha qual fica.
      </p>

      <div className="duas-versoes">
        <article className="cartao pilha">
          <p className="etiqueta">A sua, ainda não salva</p>
          <p className="trilha__nome">{conflito.suaVersao.titulo}</p>
          <ConteudoRico html={conflito.suaVersao.html} />
          <button className="botao botao--compacto" type="button" onClick={aoManterASua}>
            Manter a minha
          </button>
        </article>

        <article className="cartao pilha">
          <p className="etiqueta">A que está salva no servidor</p>
          <p className="trilha__nome">{conflito.versaoDoServidor.titulo}</p>
          <ConteudoRico html={conflito.versaoDoServidor.html} />
          <button
            className="botao botao--secundario botao--compacto"
            type="button"
            onClick={() => aoUsarDoServidor(conflito.versaoDoServidor)}
          >
            Usar a do servidor
          </button>
        </article>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ página nova */

/**
 * `/caderno/nova` — o endereço que o "Não entendi" usa para abrir um esboço.
 *
 * Ele era uma tela: o editor nascia vazio, com os campos preenchidos pela URL, e
 * só virava nota na primeira gravação. Com o editor sempre aberto e sem botão
 * de salvar, "página que ainda não existe" deixou de ter forma — então este
 * endereço **cria a página** com o que veio na URL e manda para ela. O esboço
 * continua chegando pela URL para sobreviver a um recarregamento no meio do
 * caminho (RN-08), e o item continua anexado.
 */
export function PaginaNova() {
  const navegar = useNavigate();
  const [params] = useSearchParams();
  const [erro, setErro] = useState<string | null>(null);
  const criada = useRef(false);

  useEffect(() => {
    // O modo estrito do React monta duas vezes em desenvolvimento, e sem esta
    // guarda o esboço viraria duas páginas.
    if (criada.current) return;
    criada.current = true;
    void pedir("nota.criar", {
      corpo: {
        titulo: params.get("titulo")?.trim() || SEM_TITULO,
        html: params.get("html") ?? "",
        categoriaId: params.get("categoria"),
        itensAnexados: params.getAll("item"),
      },
    })
      .then((r) => void navegar(`/caderno/${r.nota.id}`, { replace: true }))
      .catch((e: unknown) => setErro(e instanceof FalhaDaApi ? e.message : "Não foi possível criar a página."));
  }, [params, navegar]);

  if (erro) {
    return (
      <div className="tela">
        <div className="tela__corpo">
          <section className="estado estado--erro" role="alert">
            <h1 className="estado__titulo">Não deu para criar</h1>
            <p className="estado__texto">{erro}</p>
            <Link className="botao" to="/caderno">
              Voltar ao caderno
            </Link>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="tela">
      <div className="tela__corpo" aria-busy="true">
        <span className="so-leitor" role="status">
          Criando a página
        </span>
        <div className="esqueleto esqueleto--titulo" />
      </div>
    </div>
  );
}

/**
 * O caderno no celular (13d): busca, matérias em marca-texto e as páginas em
 * lista, com título, onde mora e quando mudou, e o começo do texto.
 */
function ListaMovel({
  paginas,
  criando,
  aoCriar,
}: {
  paginas: readonly { id: string; titulo: string; categoria: string[]; resumo: string; atualizadoEm: string }[];
  criando: boolean;
  aoCriar: () => void;
}) {
  const { hoje, usuario } = useSessao();
  const [termo, setTermo] = useState("");
  const [materia, setMateria] = useState<string | null>(null);
  const materias = [...new Set(paginas.map((p) => p.categoria[0] ?? SEM_MATERIA))];
  const t = termo.trim().toLocaleLowerCase("pt-BR");
  const visiveis = paginas.filter(
    (p) =>
      (materia === null || (p.categoria[0] ?? SEM_MATERIA) === materia) &&
      (t === "" || p.titulo.toLocaleLowerCase("pt-BR").includes(t) || p.resumo.toLocaleLowerCase("pt-BR").includes(t)),
  );
  return (
    <div className="caderno-movel">
      <header className="caderno-movel__topo">
        <h1 className="t-titulo">Caderno</h1>
        <button type="button" className="link-mao" disabled={criando} onClick={aoCriar}>
          <Icone glifo={IconeCriar} papel="acao" tamanho="p" />
          {criando ? "criando…" : "nova"}
        </button>
      </header>
      <label className="caderno__busca">
        <Icone glifo={IconeBuscar} papel="decorativo" tamanho="p" className="caderno__lupa" />
        <input
          className="campo__entrada caderno__campo-busca"
          type="search"
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder="buscar nas anotações"
          aria-label="Buscar nas anotações"
        />
      </label>
      {materias.length > 1 && (
        <div className="fila-mao" role="group" aria-label="Matéria">
          <button type="button" className="chip-mao" aria-pressed={materia === null} onClick={() => setMateria(null)}>
            todas
          </button>
          {materias.map((m) => (
            <button key={m} type="button" className="chip-mao" aria-pressed={materia === m} onClick={() => setMateria(m)}>
              {m}
            </button>
          ))}
        </div>
      )}
      {visiveis.length === 0 ? (
        <p className="t-ajuda">{paginas.length === 0 ? "Nenhuma página ainda. Crie a primeira." : "Nenhuma página com esse termo."}</p>
      ) : (
        <ul className="caderno-movel__lista">
          {visiveis.map((p) => (
            <li key={p.id}>
              <Link className="caderno-movel__pagina" to={`/caderno/${p.id}`}>
                <span className="t-cartao">{p.titulo.trim() === "" ? SEM_TITULO : p.titulo}</span>
                <span className="t-anotacao-p caderno-movel__onde">
                  {[p.categoria.join(" > "), dataRelativa(p.atualizadoEm, hoje, usuario.fusoHorario)].filter(Boolean).join(" · ")}
                </span>
                <span className="t-ajuda caderno-movel__resumo">{p.resumo}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
